import filter from 'lodash/filter'
import first from 'lodash/first'
import isEqual from 'lodash/isEqual'

import { PersonalisationModalTypes, ProductPersonalization } from '@Components/Personalization/Modals/Shared'
import { CartV2LineItem } from '@Domains/Cart/Feature/CartV2/CartV2'
import { ProductAttribute, ProductCrossSellAndPersonalizationContentFragment } from '@Generated/graphql'

interface PersonalizationsForlineItem {
  lineItem: CartV2LineItem
  shortName: string
}

const getPersonalizationsByTypeForLineItem = (lineItem: CartV2LineItem, personalization: 'deboss' | 'engrave') => filter(
  ((lineItem.variant?.personalizations as ProductAttribute)?.products || []) as ProductCrossSellAndPersonalizationContentFragment[],
  ['masterVariant.type.value.key', personalization],
)
const getPersonalizationsForLineItem = lineItem => lineItem.variant?.personalizations?.products || []
const getAvailablePersonalizationsForlineItem = (
  lineItem,
  personalizationLineItems,
  personalisationModalTypes: PersonalisationModalTypes,
  locale: string,
) => {
  if (lineItem.product && personalizationLineItems) {
    const personalizations: ProductPersonalization[] = getPersonalizationsForLineItem(lineItem)
    return personalizations.reduce((acc: PersonalizationsForlineItem[], personalizationProduct) => {
      if (personalizationProduct.masterVariant.type.value.key !== personalisationModalTypes) {
        return acc
      }
      const personalizationLineItem = personalizationLineItems.find(productLineItem => productLineItem.productID === personalizationProduct.id)
      const shortName = personalizationProduct.masterVariant.shortName?.value[locale]
      if (personalizationLineItem && personalizationLineItem.type.__typename === 'CartLineItemTypeUpsellPersonalization') {
        acc.push({
          lineItem: personalizationLineItem,
          shortName,
        })
      }
      return acc
    }, [])
  }
  return []
}
const getFirstPersonalizationText = (personalizations?: PersonalizationsForlineItem) => {
  const firstPersonalization = personalizations?.lineItem
  if (firstPersonalization?.type?.__typename === 'CartLineItemTypeUpsellPersonalization'
    || firstPersonalization?.type?.__typename === 'CartLineItemTypeGiftcard') {
    return firstPersonalization.type?.text
  }
  return null
}

const getPersonalizationPrice = (personalizations?: PersonalizationsForlineItem) => {
  const lineItem = personalizations?.lineItem
  const price = lineItem?.totalPrice?.format
  const normalPriceIsZero = isEqual(lineItem?.totalPrice?.plain, '0')
  const isFree = isEqual(lineItem?.totalPrice?.discount ? lineItem?.totalPrice?.discount?.plain : lineItem?.totalPrice?.plain, '0')
  const isDiscounted = !!lineItem?.totalPrice?.discount
  const discountPrice = lineItem?.totalPrice?.discount?.format
  return {
    price,
    normalPriceIsZero,
    isFree,
    isDiscounted,
    discountPrice,
  }
}
const getCrossSellPersonalizationPrice = (crossSell?: ProductCrossSellAndPersonalizationContentFragment) => {
  const crossSellPrice = crossSell?.masterVariant?.price
  const price = crossSellPrice?.format
  const normalPriceIsZero = isEqual(crossSellPrice?.plain, '0')
  const isFree = isEqual(crossSellPrice?.discount ? crossSellPrice?.discount?.plain : crossSellPrice?.plain, '0')
  const isDiscounted = !!crossSellPrice?.discount
  const discountPrice = crossSellPrice?.discount?.format
  return {
    price,
    normalPriceIsZero,
    isFree,
    isDiscounted,
    discountPrice,
  }
}

export const getLineItemPersonalizations = (lineItem, personalizationLineItems, locale) => {
  const debossCrossSells = getPersonalizationsByTypeForLineItem(lineItem, 'deboss')
  const engraveCrossSells = getPersonalizationsByTypeForLineItem(lineItem, 'engrave')

  const canDeboss = debossCrossSells.length !== 0
  const canEngrave = engraveCrossSells.length !== 0

  const debossLineItems = getAvailablePersonalizationsForlineItem(lineItem, personalizationLineItems, 'deboss', locale)
  const engraveLineItems = getAvailablePersonalizationsForlineItem(lineItem, personalizationLineItems, 'engrave', locale)

  const isDebossed = debossLineItems.length !== 0
  const isEngraved = engraveLineItems.length !== 0

  return {
    debossCrossSells,
    engraveCrossSells,
    canDeboss,
    canEngrave,
    debossLineItem: first(debossLineItems),
    engraveLineItem: first(engraveLineItems),
    engravePrice: getPersonalizationPrice(first(engraveLineItems)),
    debossPrice: getPersonalizationPrice(first(debossLineItems)),
    engraveCrossSellPrice: getCrossSellPersonalizationPrice(first(engraveCrossSells)),
    debossCrossSellPrice: getCrossSellPersonalizationPrice(first(debossCrossSells)),
    engravedText: getFirstPersonalizationText(first(engraveLineItems)),
    debossText: getFirstPersonalizationText(first(debossLineItems)),
    isDebossed,
    isEngraved,
    getFirstPersonalizationText,
    isPersonalized: isEngraved || isDebossed,
  }
}
